import { useEffect, useState } from "react";
import { Alert, Button, Dropdown, Modal, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "@/context/AuthContext";
import type { BusinessOption } from "@/models/auth";
import { loadBusinesses } from "@/services/authService";
import { readError } from "@/services/apiClient";
export function BusinessSwitcher() {
  const { session, selectBusiness } = useAuth();
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState<BusinessOption[]>(
    session?.businesses ?? [],
  );
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState("");
  const [pending, setPending] = useState<BusinessOption | null>(null);
  const current = businesses.find(
    (b) => b.businessUserId === session?.businessUserId,
  );
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!session) return;
    if (session.businesses.length) {
      setBusinesses(session.businesses);
      return;
    }
    let canceled = false;
    setLoading(true);
    void loadBusinesses()
      .then((rows) => {
        if (!canceled) setBusinesses(rows);
      })
      .catch((error) => {
        if (!canceled) setError(readError(error));
      })
      .finally(() => {
        if (!canceled) setLoading(false);
      });
    return () => {
      canceled = true;
    };
  }, [session, retry]);
  async function switchTo(business: BusinessOption) {
    setSwitching(true);
    setError("");
    setPending(null);
    try {
      await selectBusiness(business.businessUserId);
      navigate("/", { replace: true });
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Could not switch businesses.",
      );
      setSwitching(false);
    }
  }
  return (
    <>
      {businesses.length === 1 ? (
        <span className="small business-name">
          <FontAwesomeIcon icon={faBuilding} className="me-2" />
          {current?.businessName ?? businesses[0].businessName}
        </span>
      ) : (
        <Dropdown>
          <Dropdown.Toggle
            variant="outline-secondary"
            size="sm"
            disabled={switching || loading}
            aria-label="Switch business"
            className="business-switcher"
          >
            <FontAwesomeIcon icon={faBuilding} className="me-2" />
            {switching
              ? "Switching business…"
              : loading
                ? "Loading businesses…"
                : (current?.businessName ?? "Choose business")}
            {(switching || loading) && <Spinner size="sm" className="ms-2" />}
          </Dropdown.Toggle>
          <Dropdown.Menu align="end">
            <Dropdown.Header>Your businesses</Dropdown.Header>
            {businesses.map((b) => (
              <Dropdown.Item
                key={b.businessUserId}
                active={b.businessUserId === session?.businessUserId}
                onClick={() => {
                  if (b.businessUserId === session?.businessUserId) return;
                  if (
                    !window.dispatchEvent(
                      new Event("crm-before-business-switch", {
                        cancelable: true,
                      }),
                    )
                  ) {
                    setPending(b);
                    return;
                  }
                  void switchTo(b);
                }}
              >
                {b.businessName}
                {b.businessUserId === session?.businessUserId && " ✓"}
              </Dropdown.Item>
            ))}
            {!businesses.length && (
              <Dropdown.Item disabled>No businesses loaded</Dropdown.Item>
            )}
            {error && (
              <Dropdown.Item
                onClick={() => {
                  setError("");
                  setRetry((n) => n + 1);
                }}
              >
                Retry loading businesses
              </Dropdown.Item>
            )}
          </Dropdown.Menu>
        </Dropdown>
      )}
      <Modal show={!!error} onHide={() => setError("")}>
        <Modal.Header closeButton>
          <Modal.Title>Business switch unavailable</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">{error}</Alert>
          <p>Your current business remains open.</p>
        </Modal.Body>
      </Modal>
      <Modal show={!!pending} onHide={() => setPending(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Switch business?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          You have unsaved form changes. Switching to {pending?.businessName}{" "}
          will discard them.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPending(null)}>
            Keep editing
          </Button>
          <Button onClick={() => pending && void switchTo(pending)}>
            Discard and switch
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
